import type { GeometryElement, Value, PointTarget, EdgeTarget } from '../geometrypad.model';
import type { TriangleElement } from '../elements/shapes/triangle/triangle.model';
import type { RectangleElement } from '../elements/shapes/rectangle/rectangle.model';
import type { SquareElement } from '../elements/shapes/square/square.model';
import type { CircleElement } from '../elements/shapes/circle/circle.model';
import type { ParallelogramElement } from '../elements/shapes/parallelogram/parallelogram.model';
import type { RhombusElement } from '../elements/shapes/rhombus/rhombus.model';
import type { TrapezoidElement } from '../elements/shapes/trapezoid/trapezoid.model';
import type { ArcElement } from '../elements/shapes/arc/arc.model';
import type { PointElement, LineElement, PolygonElement } from '../elements/shapes/primitives/primitives.model';
import type { PortRegistry } from '../port';
import { resolveValue } from '../port';
import { solveCircle } from '../analytics/circle-solver';
import { solveRectangle } from '../analytics/rectangle-solver';
import { solveSquare } from '../analytics/square-solver';
import { solveTriangle } from '../analytics/triangle-solver';
import { solveParallelogram } from '../analytics/parallelogram-solver';
import { solveRhombus } from '../analytics/rhombus-solver';
import { solveTrapezoid } from '../analytics/trapezoid-solver';
import { solveArc } from '../analytics/arc-solver';
import type { SolvedShape } from '../analytics/solver.model';
import type { RigidTransform } from './placement.model';
import { applyTransform } from './placement.model';

/**
 * Result of canonically solving and placing a shape.
 * Contains the solved shape data and the vertex map.
 */
export interface CanonicalResult {
  solved: SolvedShape | null;
  vertices: Record<string, [number, number]>;
  /** True if vertex positions were determined from PointTarget/EdgeTarget params */
  isPositioned: boolean;
  /** Transform from canonical → world (only when isPositioned is true) */
  transform?: RigidTransform;
}

// ---------------------------------------------------------------------------
// Param classification helpers
// ---------------------------------------------------------------------------

/** Check if a value is a PointTarget object (not a scalar Value) */
function isPointTarget(val: unknown): val is PointTarget {
  if (typeof val !== 'object' || val === null) {
    return false;
  }
  return 'x' in val && 'y' in val && !('ref' in val);
}

/** Check if a value is an EdgeTarget object */
function isEdgeTarget(val: unknown): val is EdgeTarget {
  if (typeof val !== 'object' || val === null) {
    return false;
  }
  return 'ref' in val && !('x' in val);
}

/** Check if a value is a scalar Value (number or string expression) */
function isScalarValue(val: unknown): val is Value {
  return typeof val === 'number' || typeof val === 'string';
}

/**
 * Resolve a PointTarget to world coordinates.
 */
function resolvePointTarget(target: PointTarget, registry: PortRegistry): [number, number] {
  return [resolveValue(target.x, registry), resolveValue(target.y, registry)];
}

/**
 * Separate params into scalars, vertex positions, and edge targets.
 * Returns resolved numeric scalars and raw positional data.
 */
function separateParams(
  params: Record<string, unknown>,
  vertexKeys: readonly string[],
  edgeKeys: readonly string[],
  registry: PortRegistry,
): {
  scalars: Record<string, number>;
  vertices: Record<string, [number, number]>;
  edges: Record<string, EdgeTarget>;
  hasPositionalParams: boolean;
} {
  const scalars: Record<string, number> = {};
  const vertices: Record<string, [number, number]> = {};
  const edges: Record<string, EdgeTarget> = {};
  let hasPositionalParams = false;

  for (const [key, val] of Object.entries(params)) {
    if (val === undefined) {
      continue;
    }

    if (vertexKeys.includes(key) && isPointTarget(val)) {
      vertices[key] = resolvePointTarget(val, registry);
      hasPositionalParams = true;
    } else if (edgeKeys.includes(key) && isEdgeTarget(val)) {
      edges[key] = val;
      hasPositionalParams = true;
    } else if (isScalarValue(val)) {
      scalars[key] = resolveValue(val, registry);
    }
  }

  return { scalars, vertices, edges, hasPositionalParams };
}

// ---------------------------------------------------------------------------
// Edge-to-vertex resolution
// ---------------------------------------------------------------------------

/**
 * Edge-to-vertex mappings per shape type.
 * Each edge maps to [startVertex, endVertex] in the canonical naming.
 */
const EDGE_TO_VERTICES: Record<string, Record<string, [string, string]>> = {
  Triangle: { AB: ['A', 'B'], BC: ['B', 'C'], CA: ['C', 'A'] },
  Rectangle: { bottom: ['A', 'B'], right: ['B', 'C'], top: ['C', 'D'], left: ['D', 'A'] },
  Square: { bottom: ['A', 'B'], right: ['B', 'C'], top: ['C', 'D'], left: ['D', 'A'] },
  Parallelogram: { bottom: ['A', 'B'], right: ['B', 'C'], top: ['C', 'D'], left: ['D', 'A'] },
  Rhombus: { bottom: ['A', 'B'], right: ['B', 'C'], top: ['C', 'D'], left: ['D', 'A'] },
  Trapezoid: { bottom: ['A', 'B'], right: ['B', 'C'], top: ['C', 'D'], left: ['D', 'A'] },
};

/**
 * Resolve EdgeTargets into vertex positions and merge them into the vertices map.
 *
 * For `side: 'exterior'`: the edge direction is reversed so that the canonical
 * "up" direction of the new shape faces away from the source shape's interior.
 * For `side: 'interior'` or unspecified: same direction.
 */
function resolveEdgeTargets(
  edges: Record<string, EdgeTarget>,
  elementType: string,
  vertices: Record<string, [number, number]>,
  registry: PortRegistry,
): void {
  const vertexMap = EDGE_TO_VERTICES[elementType];
  if (!vertexMap) {
    return;
  }

  for (const [edgeName, target] of Object.entries(edges)) {
    const mapping = vertexMap[edgeName];
    if (!mapping) {
      continue;
    }

    const edge = registry.resolveEdge(target.ref);
    const [v0, v1] = mapping;

    if (target.side === 'exterior') {
      // Reverse direction: canonical counterclockwise places remaining vertices on exterior
      vertices[v0] = edge.end as [number, number];
      vertices[v1] = edge.start as [number, number];
    } else {
      vertices[v0] = edge.start as [number, number];
      vertices[v1] = edge.end as [number, number];
    }
  }
}

// ---------------------------------------------------------------------------
// Rigid transform computation
// ---------------------------------------------------------------------------

/**
 * Compute rigid transform from canonical vertices to world-space vertices.
 * Uses the first two matched vertices to determine rotation and translation.
 */
function computeTransformFromVertices(
  canonical: Record<string, [number, number]>,
  world: Record<string, [number, number]>,
): RigidTransform | undefined {
  // Find matching vertex pairs
  const pairs: Array<{ canonical: [number, number]; world: [number, number] }> = [];
  for (const key of Object.keys(world)) {
    if (canonical[key]) {
      pairs.push({ canonical: canonical[key], world: world[key] });
    }
  }

  if (pairs.length === 0) {
    return undefined;
  }

  if (pairs.length === 1) {
    // Single point: translation only (rotation = 0)
    const [cx, cy] = pairs[0].canonical;
    const [wx, wy] = pairs[0].world;
    return { rotation: 0, translation: [wx - cx, wy - cy] };
  }

  // Two+ points: compute rotation from first pair's vector
  const [c0, c1] = [pairs[0].canonical, pairs[1].canonical];
  const [w0, w1] = [pairs[0].world, pairs[1].world];

  const canonicalAngle = Math.atan2(c1[1] - c0[1], c1[0] - c0[0]);
  const worldAngle = Math.atan2(w1[1] - w0[1], w1[0] - w0[0]);
  const rotation = worldAngle - canonicalAngle;

  // Translation: apply rotation to canonical[0], find delta to world[0]
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const rotated: [number, number] = [cos * c0[0] - sin * c0[1], sin * c0[0] + cos * c0[1]];
  const translation: [number, number] = [w0[0] - rotated[0], w0[1] - rotated[1]];

  return { rotation, translation };
}

// ---------------------------------------------------------------------------
// Main dispatch
// ---------------------------------------------------------------------------

/**
 * Solve and canonically place an element.
 * Resolves any Value expressions through the port registry.
 */
export function canonicalPlace(element: GeometryElement, registry: PortRegistry): CanonicalResult {
  switch (element.type) {
    case 'Triangle':
      return placeTriangle(element, registry);
    case 'Rectangle':
      return placeRectangle(element, registry);
    case 'Square':
      return placeSquare(element, registry);
    case 'Circle':
      return placeCircle(element, registry);
    case 'Arc':
      return placeArc(element, registry);
    case 'Parallelogram':
      return placeParallelogram(element, registry);
    case 'Rhombus':
      return placeRhombus(element, registry);
    case 'Trapezoid':
      return placeTrapezoid(element, registry);
    case 'Point':
      return placePoint(element, registry);
    case 'Line':
      return placeLine(element, registry);
    case 'Polygon':
      return placePolygon(element, registry);
    case 'LengthAnnotation':
    case 'ArrowAnnotation':
    case 'Diagonal':
    case 'Altitude':
    case 'Median':
    case 'Midsegment':
      return { solved: null, vertices: {}, isPositioned: false };
    default:
      return { solved: null, vertices: {}, isPositioned: false };
  }
}

// ---------------------------------------------------------------------------
// Shape-specific placement
// ---------------------------------------------------------------------------

const TRIANGLE_VERTEX_KEYS = ['A', 'B', 'C'] as const;
const TRIANGLE_EDGE_KEYS = ['AB', 'BC', 'CA'] as const;

function placeTriangle(element: TriangleElement, registry: PortRegistry): CanonicalResult {
  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    TRIANGLE_VERTEX_KEYS,
    TRIANGLE_EDGE_KEYS,
    registry,
  );

  // Resolve edge targets → vertex pairs
  resolveEdgeTargets(edges, 'Triangle', vertices, registry);

  const solved = solveTriangle(scalars);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

const QUAD_VERTEX_KEYS = ['A', 'B', 'C', 'D'] as const;
const QUAD_EDGE_KEYS = ['bottom', 'right', 'top', 'left'] as const;

function placeRectangle(element: RectangleElement, registry: PortRegistry): CanonicalResult {
  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    QUAD_VERTEX_KEYS,
    QUAD_EDGE_KEYS,
    registry,
  );

  resolveEdgeTargets(edges, 'Rectangle', vertices, registry);

  const solved = solveRectangle(scalars);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

function placeSquare(element: SquareElement, registry: PortRegistry): CanonicalResult {
  if (!element.params) {
    const solved = solveSquare(1);
    return { solved, vertices: { ...solved.vertices }, isPositioned: false };
  }

  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    QUAD_VERTEX_KEYS,
    QUAD_EDGE_KEYS,
    registry,
  );

  resolveEdgeTargets(edges, 'Square', vertices, registry);

  const s = scalars['s'] ?? 1;
  const solved = solveSquare(s);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

function placeCircle(element: CircleElement, registry: PortRegistry): CanonicalResult {
  const params = element.params;
  const resolvedValues: Record<string, number | [number, number]> = {};

  // Resolve scalar params
  if (params.r !== undefined) {
    resolvedValues['r'] = resolveValue(params.r, registry);
  }

  // Resolve center PointTarget
  let isPositioned = false;
  if (params.center !== undefined) {
    const center = resolvePointTarget(params.center, registry);
    resolvedValues['cx'] = center[0];
    resolvedValues['cy'] = center[1];
    isPositioned = true;
  }

  // Resolve diameter points
  if (params.diameterA !== undefined) {
    resolvedValues['diameterA'] = resolvePointTarget(params.diameterA, registry);
    isPositioned = true;
  }
  if (params.diameterB !== undefined) {
    resolvedValues['diameterB'] = resolvePointTarget(params.diameterB, registry);
    isPositioned = true;
  }

  const solved = solveCircle(resolvedValues);
  return {
    solved,
    vertices: { center: solved.center },
    isPositioned,
  };
}

function placePoint(element: PointElement, registry: PortRegistry): CanonicalResult {
  const x = resolveValue(element.params.x, registry);
  const y = resolveValue(element.params.y, registry);
  return {
    solved: null,
    vertices: { point: [x, y] },
    isPositioned: true,
  };
}

function placeLine(element: LineElement, registry: PortRegistry): CanonicalResult {
  const from = resolvePointTarget(element.params.from, registry);
  const to = resolvePointTarget(element.params.to, registry);
  return {
    solved: null,
    vertices: { from, to },
    isPositioned: true,
  };
}

function placePolygon(element: PolygonElement, registry: PortRegistry): CanonicalResult {
  const vertices: Record<string, [number, number]> = {};
  element.params.vertices.forEach((v, i) => {
    vertices[`point[${i}]`] = resolvePointTarget(v, registry);
  });
  return {
    solved: null,
    vertices,
    isPositioned: true,
  };
}

function placeParallelogram(element: ParallelogramElement, registry: PortRegistry): CanonicalResult {
  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    QUAD_VERTEX_KEYS,
    QUAD_EDGE_KEYS,
    registry,
  );

  resolveEdgeTargets(edges, 'Parallelogram', vertices, registry);

  const solved = solveParallelogram(scalars);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

function placeRhombus(element: RhombusElement, registry: PortRegistry): CanonicalResult {
  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    QUAD_VERTEX_KEYS,
    QUAD_EDGE_KEYS,
    registry,
  );

  resolveEdgeTargets(edges, 'Rhombus', vertices, registry);

  const s = scalars['s'] ?? 1;
  const angle = scalars['angle'] ?? 60;
  const solved = solveRhombus(s, angle);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

function placeTrapezoid(element: TrapezoidElement, registry: PortRegistry): CanonicalResult {
  const { scalars, vertices, edges, hasPositionalParams } = separateParams(
    element.params as Record<string, unknown>,
    QUAD_VERTEX_KEYS,
    QUAD_EDGE_KEYS,
    registry,
  );

  resolveEdgeTargets(edges, 'Trapezoid', vertices, registry);

  const solved = solveTrapezoid(scalars);
  const canonicalVerts = { ...solved.vertices };

  if (hasPositionalParams && Object.keys(vertices).length > 0) {
    const transform = computeTransformFromVertices(canonicalVerts, vertices);
    if (transform) {
      const worldVerts: Record<string, [number, number]> = {};
      for (const [name, coord] of Object.entries(canonicalVerts)) {
        worldVerts[name] = applyTransform(coord, transform);
      }
      return { solved, vertices: worldVerts, isPositioned: true, transform };
    }
  }

  return { solved, vertices: canonicalVerts, isPositioned: false };
}

function placeArc(element: ArcElement, registry: PortRegistry): CanonicalResult {
  const params = element.params;
  const resolvedValues: Record<string, number> = {};

  // Resolve scalar params
  if (params.r !== undefined) {
    resolvedValues['r'] = resolveValue(params.r, registry);
  } else {
    resolvedValues['r'] = 3;
  }
  if (params.startAngle !== undefined) {
    resolvedValues['startAngle'] = resolveValue(params.startAngle, registry);
  }
  if (params.endAngle !== undefined) {
    resolvedValues['endAngle'] = resolveValue(params.endAngle, registry);
  }
  if (params.centralAngle !== undefined) {
    resolvedValues['centralAngle'] = resolveValue(params.centralAngle, registry);
  }

  // Resolve center PointTarget
  let isPositioned = false;
  if (params.center !== undefined) {
    const center = resolvePointTarget(params.center, registry);
    resolvedValues['cx'] = center[0];
    resolvedValues['cy'] = center[1];
    isPositioned = true;
  }

  const solved = solveArc(resolvedValues);

  // Compute world-space vertex positions (startPoint/endPoint/midPoint are relative to origin)
  const cx = solved.center[0];
  const cy = solved.center[1];
  const startPoint: [number, number] = [cx + solved.startPoint[0], cy + solved.startPoint[1]];
  const endPoint: [number, number] = [cx + solved.endPoint[0], cy + solved.endPoint[1]];
  const midPoint: [number, number] = [cx + solved.midPoint[0], cy + solved.midPoint[1]];

  return {
    solved,
    vertices: { center: solved.center, startPoint, endPoint, midPoint },
    isPositioned,
  };
}
